import React, {useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import styles from './AppHeader.module.css';
import LogoMark from '../marketing/components/Logo';
import Button from '../marketing/components/Button';
import AccountMenu from './AccountMenu';
import useDismissableMenu from './useDismissableMenu';
import {MenuPanel, MenuLink} from './MenuPanel';
import {MenuIcon, CloseIcon, PlusIcon, ChevronDownIcon} from '../marketing/icons/Icons';
import {CREATE_PAGE_ROUTE, HOW_TO_ROUTE, CONTACT_ROUTE} from '../marketing/routes';

const PAGES_ROUTE = '/pages';

const isActive = (pathname, to) => pathname.toLowerCase() === to.toLowerCase();

// The "Pages ▾" / "Org ▾" nav dropdowns - a button that opens a MenuPanel,
// styled as a nav pill rather than the account/more menus' icon trigger.
const NavMenu = ({label, active, children}) => {
    const [open, setOpen] = useState(false);
    const {triggerRef, menuRef} = useDismissableMenu(open, () => setOpen(false));

    return (
        <div className={styles.navMenuWrap}>
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                {label}<ChevronDownIcon/>
            </button>
            {open && (
                <MenuPanel ref={menuRef} label={label} align="start">
                    {children}
                </MenuPanel>
            )}
        </div>
    );
};

const orgMenuItems = (authentication) => (
    authentication?.organisation ? (
        <MenuLink to={`/organisations/${authentication.organisation.id}`}>{authentication.organisation.name}</MenuLink>
    ) : (
        <MenuLink to="/createOrganisation">Create org</MenuLink>
    )
);

/**
 * The app's top bar for every logged-in page except the editor (which has
 * its own breadcrumb bar - see AppBar.js). Replaces the old marketing
 * Header inside the logged-in app.
 */
const AppHeader = ({authentication}) => {
    const {pathname} = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const {triggerRef, menuRef} = useDismissableMenu(menuOpen, () => setMenuOpen(false));

    const pagesActive = isActive(pathname, PAGES_ROUTE) || isActive(pathname, CREATE_PAGE_ROUTE);
    const orgActive = pathname.toLowerCase().startsWith('/organisations') || isActive(pathname, '/createOrganisation');
    const howToActive = isActive(pathname, HOW_TO_ROUTE);
    const contactActive = isActive(pathname, CONTACT_ROUTE);

    return (
        <header className={styles.bar}>
            <div className={styles.left}>
                <Link to={PAGES_ROUTE} aria-label="Ovalview, my pages" className={styles.logoLink}>
                    <LogoMark width={36} height={20}/>
                    <span className={styles.wordmark}>Ovalview</span>
                </Link>
                <nav aria-label="App" className={styles.nav}>
                    <NavMenu label="Pages" active={pagesActive}>
                        <MenuLink to={PAGES_ROUTE}>My pages</MenuLink>
                        <MenuLink to={CREATE_PAGE_ROUTE}>Create page</MenuLink>
                    </NavMenu>
                    <NavMenu label="Org" active={orgActive}>
                        {orgMenuItems(authentication)}
                    </NavMenu>
                    <Link
                        to={HOW_TO_ROUTE}
                        className={`${styles.navLink} ${howToActive ? styles.navLinkActive : ''}`}
                        aria-current={howToActive ? 'page' : undefined}
                    >
                        How to
                    </Link>
                    <Link
                        to={CONTACT_ROUTE}
                        className={`${styles.navLink} ${contactActive ? styles.navLinkActive : ''}`}
                        aria-current={contactActive ? 'page' : undefined}
                    >
                        Contact
                    </Link>
                </nav>
            </div>

            <div className={styles.right}>
                <Button to={CREATE_PAGE_ROUTE} variant="outline" size="nav" aria-label="New page"
                        className={styles.newPageButton}>
                    <PlusIcon/><span className={styles.newPageLabel}>New page</span>
                </Button>
                <AccountMenu authentication={authentication}/>
                <div className={styles.menuWrap}>
                    <button
                        ref={triggerRef}
                        type="button"
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        className={styles.menuButton}
                        onClick={() => setMenuOpen((open) => !open)}
                    >
                        {menuOpen ? <CloseIcon/> : <MenuIcon/>}
                    </button>
                    {menuOpen && (
                        <MenuPanel ref={menuRef} label="Main">
                            <MenuLink to={PAGES_ROUTE}>My pages</MenuLink>
                            <MenuLink to={CREATE_PAGE_ROUTE}>Create page</MenuLink>
                            {orgMenuItems(authentication)}
                            <MenuLink to={HOW_TO_ROUTE}>How to</MenuLink>
                            <MenuLink to={CONTACT_ROUTE}>Contact</MenuLink>
                        </MenuPanel>
                    )}
                </div>
            </div>
        </header>
    );
};

export default AppHeader;
